import classes from "./EditStudent.module.scss"
import Header from "../../components/Header/Header"
import clsx from "clsx"
import blueLogo from "../../assets/blue-logo.svg"
import { useEffect, useState } from "react"
import { BASE_URL, patch } from "../../utils/requests"
import FormBuilder, {
  FieldsGroup,
} from "../../components/FormBuilder/FormBuilder"
import { isValidIdNumber } from "./StudentJoin"
import { useSettings } from "../../contexts/SettingsContext"
import { useNavigate } from "react-router-dom"
import axios from "axios"

const fields: FieldsGroup[] = [
  {
    id: "personal",
    label: "פרטים אישיים",
    fields: [
      {
        id: "firstName",
        name: "firstName",
        label: "שם פרטי",
        type: "text",
        required: true,
      },
      {
        id: "lastName",
        name: "lastName",
        label: "שם משפחה",
        type: "text",
        required: true,
      },
      {
        id: "idNumber",
        name: "idNumber",
        label: "תעודת זהות",
        type: "text",
        required: true,
      },
      {
        id: "dateOfBirth",
        name: "dateOfBirth",
        label: "תאריך לידה",
        type: "date",
        required: true,
      },
    ],
  },
  {
    id: "address",
    label: "כתובת",
    fields: [
      {
        id: "city",
        name: "city",
        label: "עיר",
        type: "text",
        required: true,
      },
      {
        id: "street",
        name: "street",
        label: "רחוב",
        type: "text",
        required: true,
      },
      {
        id: "streetNumber",
        name: "streetNumber",
        label: "מספר בית",
        type: "text",
        required: true,
      },
    ],
  },
  {
    id: "medical",
    label: "פרטים רפואיים",
    fields: [
      {
        id: "hasGlasses",
        name: "hasGlasses",
        label: "מרכיב משקפיים?",
        type: "checkbox",
      },
      {
        id: "eyeExamDate",
        name: "eyeExamDate",
        label: "תאריך בדיקת עיניים",
        type: "date",
        required: false,
      },
      {
        id: "medicalExamDate",
        name: "medicalExamDate",
        label: "תאריך מילוי שאלון רפואי",
        type: "date",
        required: false,
      },
    ],
  },
  {
    id: "attachment",
    label: "מסמכים נדרשים",
    fields: [
      {
        id: "medicalDocument",
        name: "medicalDocument",
        label: "אישור בדיקת עיניים",
        type: "file",
        accepts: "image/*,.pdf",
        required: false,
      },
    ],
  }
]

const EditStudent = () => {
  const {
    settings: { token, student },
  } = useSettings()
  const navigate = useNavigate()

  useEffect(() => {
    if (!token) {
      sessionStorage.setItem("loginRedirect", "/student/edit")
      navigate("/login/phone", { replace: true })
    }
  }, [token, navigate])

  const formatDate = (date?: Date) => {
    if (!date) return ""
    const d = new Date(date)
    if (isNaN(d.getTime())) return ""
    return d.toISOString().split("T")[0]
  }

  const [formDone, setFormDone] = useState(false)

  const [values, setValues] = useState<object>({
    hasGlasses: false,
  })

  useEffect(() => {
    if (student) {
      setValues((prev) => ({
        ...prev,
        firstName: student.firstName ?? "",
        lastName: student.lastName ?? "",
        idNumber: student.idNumber ?? "",
        dateOfBirth: formatDate(student.dateOfBirth),
        city: student.address?.city ?? "",
        street: student.address?.street ?? "",
        streetNumber: student.address?.streetNumber ?? "",
        hasGlasses: student.glasses ?? false,
        eyeExamDate: formatDate(student.eyeExamDate),
        medicalExamDate: formatDate(student.medicalExamDate),
      }))
    }
  }, [student])
  const [loadingStatus, setLoadingStatus] = useState<string>("")
  const [errors, setErrors] = useState<Record<string, string>>({})

  const uploadAFile = async (
    type: "medicalDocument",
    studentUID: number,
  ) => {
    const formData = new FormData()
    formData.append("file", values[`${type}File`])
    formData.append("path", values[type])

    try {
      await axios.post(`${BASE_URL}/students/upload`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          "X-Student-UID": studentUID,
          "X-File": type,
        },
      })
    } catch { }
  }

  const checkRequiredFields = () => {
    const requiredFields = fields
      .map((field) => field.fields.filter((f) => f.required).map((f) => f.name))
      .flat()

    const missingFields: string[] = []

    requiredFields.forEach((field) => {
      if (!values[field]?.toString().trim()) {
        missingFields.push(field)
      }
    })

    return missingFields
  }

  const scrollToField = (fieldName: string) => {
    let inputName = fieldName
    if (fieldName === "general") {
      inputName = "submit-form"
    }
    const field = document.querySelector(`#${inputName}`)
    if (field) {
      field.scrollIntoView({ behavior: "smooth", block: "center" })
    }
  }

  const onSubmit = async (values: object) => {
    const missingFields = checkRequiredFields()

    const errors: Record<string, string> = {}
    missingFields.forEach((field) => (errors[field] = "שדה חובה"))
    if (!isValidIdNumber(values["idNumber"])) {
      errors["idNumber"] = "יש להזין תעודת זהות תקינה"
    }
    setErrors(errors)

    if (Object.keys(errors).length > 0) {
      const fieldName = missingFields[0] ?? "idNumber"
      scrollToField(fieldName)
      return
    }

    setLoadingStatus("שולח את הפרטים")

    const payload = {
      firstName: values["firstName"],
      lastName: values["lastName"],
      idNumber: values["idNumber"],
      dateOfBirth: values["dateOfBirth"],
      city: values["city"],
      street: values["street"],
      streetNumber: values["streetNumber"],
      hasGlasses: values["hasGlasses"],
      eyeCheckDate: values["eyeExamDate"],
      medicalExamDate: values["medicalExamDate"],
      filledFormOnline: values["filledFormOnline"],
      agreedToAge: values["agreedToAge"],
    }

    const data = await patch(`${BASE_URL}/student/complete-details`, payload)

    if (data?.error) {
      if (!!data.error.field) {
        setErrors((prevErrors) => ({
          ...prevErrors,
          [data.error.field]: data.error.message,
        }))
        scrollToField(data.error.field)
      } else {
        setErrors((prevErrors) => ({
          ...prevErrors,
          general: data.error.message,
        }))
      }
      setLoadingStatus("")
      return
    }

    console.log({ medicalDocumentFile: values["medicalDocumentFile"], medicalDocument: values["medicalDocument"], student: student?.uid })
    if (values["medicalDocumentFile"] && student?.uid) {
      setLoadingStatus("מעלה את אישור בדיקת העיניים")
      await uploadAFile("medicalDocument", student.uid)
    }

    setLoadingStatus("")
    setFormDone(true)
  }

  if (!token) {
    return null
  }

  if (formDone) {
    return (
      <div className={classes.generalMessage}>
        <a
          className={classes.logoWrapper}
          href="https://diodrive.co.il"
          target="_blank"
          rel="noreferrer"
        >
          <img src={blueLogo} className={classes.logo} alt="" />
        </a>
        <h2>הפרטים עודכנו בהצלחה</h2>
        <p>תודה רבה על מילוי הטופס, הפרטים שלך עודכנו במערכת</p>
      </div>
    )
  }

  return (
    <>
      {loadingStatus && <div className={classes.loading}>{loadingStatus}</div>}
      <div className={classes.root}>
        <div className={clsx(classes.header, "padded")}>
          <a
            className={classes.logoWrapper}
            href="https://diodrive.co.il"
            target="_blank"
            rel="noreferrer"
          >
            <img src={blueLogo} className={classes.logo} alt="" />
          </a>
          <Header
            title="עדכון פרטי תלמיד"
            subtitle="מלאו את הטופס כדי לעדכן את הפרטים שלכם"
          />
        </div>
        <div className={clsx(classes.content, "padded")}>
          <FormBuilder
            groups={fields}
            values={values}
            onChange={setValues}
            onSubmit={onSubmit}
            withCaptcha={false}
            errors={errors}
            onErrorsChange={setErrors}
          />
        </div>
      </div>
    </>
  )
}

export default EditStudent
